import { Box, Stack, Typography } from '@mui/material'
import { pieClasses, PieChart } from '@mui/x-charts'
import { IReport } from '../../interface/IReport'
import { useSelector } from 'react-redux'
import { IProject } from '../../interface/IProjects'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IUser } from '@/interface/IUser'

const StatisticsProjects = (props: any) => {
    const { colorScheme } = props as { colorScheme: string[] }
    const { colorExtendScheme } = props as { colorExtendScheme: string[] }
    const report: IReport[] = useSelector((state: any) => state.report?.value)
    const projects: IProject[] = useSelector((state: any) => state.projects?.value)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const { t } = useTranslation()
    const isTimecardUser = Boolean(user?.worker?.mserp_usetimecard)

    const [drillDown, setDrillDown] = useState<number>(-1)

    const groupedProjects = report
        .flatMap((entry) => entry.calendarLines)
        .reduce((acc, line) => {
            const activityCategory = (line.mserp_indirectactivitycategoryid ?? '').toUpperCase()
            const projectActivity = activityCategory !== 'SYSTEM'
            const projectId = isTimecardUser
                ? (projectActivity ? line.mserp_projectid : line.mserp_timeandattendancejobid)
                : line.mserp_projectid
            const activityId = isTimecardUser ? line.mserp_timeandattendancejobid : line.mserp_activitynumber
            const projectName = projects.find((item) => item.mserp_projectid === projectId)?.mserp_projectname

            if (!projectId || !activityId) {
                return acc
            }

            acc[projectId] ??= { projectId, projectName, activities: {} }

            if (!acc[projectId].activities[activityId]) {
                const activityName = isTimecardUser
                    ? (projectActivity ? projects.find((item) => item.mserp_projectid === line.mserp_projectid)?.activities?.find((act: any) => act.mserp_jobid === line.mserp_timeandattendancejobid)?.mserp_description : projectName)
                    : projects.find((item) => item.mserp_projectid === line.mserp_projectid)?.activities?.find((act: any) => act.mserp_activitynumber === line.mserp_activitynumber)?.mserp_description

                acc[projectId].activities[activityId] = {
                    activityId,
                    activityName,
                    totalTime: 0,
                    category: line.mserp_indirectactivitycategoryid ?? line.mserp_category,
                }
            }

            const lineHours = isTimecardUser ? ((line.mserp_calculatedtimeseconds ?? 0) / 60.0 / 60.0) : (line.mserp_hours ?? 0)
            acc[projectId].activities[activityId].totalTime += lineHours

            return acc
        }, {})

    const groupedProjectsArray = Object.values(groupedProjects)
        .map((proj: any) => ({
            ...proj,
            activities: Object.values(proj.activities).map((act: any) => ({ ...act, totalTime: Math.round(act.totalTime * 100) / 100 })),
            totalTime: Math.round(Object.values(proj.activities).reduce((sum: number, activity: any) => sum + activity.totalTime, 0) * 100) / 100,
        }))
        .filter((line) => line.projectId)
        .filter((entry) => entry.projectId !== `${process.env.REACT_APP_JID_START}` && entry.projectId !== `${process.env.REACT_APP_JID_STOP}` && entry.projectId !== `${process.env.REACT_APP_JID_BREAK}`)

    return (
        <>
            <Typography variant="h6">{t('report.distributionofhours')}</Typography>

            <Typography variant="body2" color="textSecondary">
                {drillDown === -1 ? t('report.distributionofhoursinfo') : `${t('report.selectedproject')} ${groupedProjectsArray[drillDown]?.projectId} - ${groupedProjectsArray[drillDown]?.projectName}`}
            </Typography>

            <Stack direction="row" sx={{ width: '100%', textAlign: 'center' }} spacing={2}>
                <Box>
                    <PieChart
                        series={[
                            {
                                data:
                                    drillDown === -1
                                        ? groupedProjectsArray.map((entry, i) => ({
                                              id: i,
                                              value: entry.totalTime,
                                              label: `${entry.projectId} - ${entry.projectName}`,
                                          }))
                                        : groupedProjectsArray[drillDown]?.activities
                                              ?.filter((line: { totalTime: number }) => line.totalTime > 0)
                                              .map(
                                                  (
                                                      entry: {
                                                          activityName: any
                                                          activityId: any
                                                          totalTime: any
                                                          Project: any
                                                          ProjectName: any
                                                      },
                                                      i: any
                                                  ) => ({
                                                      id: i,
                                                      value: entry.totalTime,
                                                      label: `${entry.activityId} - ${entry.activityName}`,
                                                  })
                                              ),
                                innerRadius: 35,
                                highlightScope: { fade: 'global', highlight: 'item' },
                                faded: { innerRadius: 30, additionalRadius: -15, color: 'gray' },
                                valueFormatter: (item: { value: number }) => `${item.value}h`,
                                arcLabel: (item) => `${item.value}h`,
                            },
                        ]}
                        sx={{
                            [`& .${pieClasses.arcLabel}`]: {
                                fontWeight: 'bold',
                                fontSize: 14,
                                fill: 'white',
                                stroke: 'rgba(0,0,0,0.8)',
                                strokeWidth: 3,
                                paintOrder: 'stroke',
                            },
                        }}
                        height={400}
                        width={1000}
                        colors={drillDown === -1 ? colorScheme : colorExtendScheme}
                        margin={{ bottom: 100, top: 20, left: 0, right: 0 }}
                        slotProps={{
                            legend: {
                                direction: 'horizontal',
                                position: { vertical: 'bottom', horizontal: 'center' },
                            },
                        }}
                        onItemClick={(event, value) => setDrillDown(drillDown >= 0 ? -1 : value.dataIndex)}
                    />
                </Box>
            </Stack>
        </>
    )
}

export default StatisticsProjects
